import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { getPosts, getCategories } from "../services/blogService";
import StoryCard from "../components/blog/StoryCard";
import "../styles/blog/StoriesList.css";
import Seo from "../seo/Seo";
import { categoryName, categoryIntro } from "../utils/categories";

const StoriesList = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [postsData, setPostsData] = useState({
    posts: [],
    total: 0,
    page: 1,
    limit: 9,
    totalPages: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  // ?category=making-waves selects a category, so a filtered list can be linked
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "");
  const [categories, setCategories] = useState([]);
  const heroRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const cats = await getCategories();
        setCategories(cats);
      } catch (err) {
        console.error("Error fetching categories", err);
      }
    };
    fetchCategories();
  }, [lang]);

  useEffect(() => {
    let isMounted = true;
    
    const loadPosts = async () => {
      try {
        // no loading screen on refetch: it would unmount the search box mid-typing
        setError(null);
        const data = await getPosts({ page: currentPage, limit: 9, category: selectedCategory || undefined, query: searchQuery });
        
        if (isMounted) {
          setPostsData(data);
        }
      } catch (err) {
        if (isMounted) {
          setError(t("stories.loadError"));
          console.error("Error loading posts:", err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPosts();

    return () => {
      isMounted = false;
    };
  }, [currentPage, selectedCategory, searchQuery, lang, t]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        setMousePosition({ x, y });
      }
    };

    const handleMouseLeave = () => {
      setMousePosition({ x: 0, y: 0 });
    };

    const hero = heroRef.current;
    if (hero) {
      hero.addEventListener('mousemove', handleMouseMove);
      hero.addEventListener('mouseleave', handleMouseLeave);
      return () => {
        hero.removeEventListener('mousemove', handleMouseMove);
        hero.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, []);

  // search, category filter and pagination all happen in getPosts
  const filteredPosts = postsData.posts;

  const handleCategoryChange = (category) => {
    const next = category === selectedCategory ? "" : category;
    setSelectedCategory(next);
    setCurrentPage(1);
    setSearchParams(next ? { category: next } : {}, { replace: true });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= postsData.totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const seo = <Seo page="stories" path="/stories" override={{ type: "blog" }} />;

  if (loading) {
    return (
      <div>
        {seo}
        <div className="hero_area"></div>
        <section className="stories-wrapper layout_padding">
          <div className="container">
            <div className="stories-container">
              <div className="loading-state">{t("stories.loading")}</div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="hero_area"></div>
        <section className="stories-wrapper layout_padding">
          <div className="container">
            <div className="stories-container">
              <div className="error-state">{error}</div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      {seo}
      <div 
        className="stories-hero" 
        ref={heroRef}
      >
        <div className="stories-hero-overlay"></div>
        <div 
          className="stories-hero-mouse-effect"
          style={{
            left: `${mousePosition.x}px`,
            top: `${mousePosition.y}px`,
          }}
        ></div>
        <div className="stories-hero-content">
          <h1>{t("stories.title")}</h1>
          <p>{t("stories.subtitle")}</p>
        </div>
      </div>

      <section className="stories-wrapper layout_padding">
        <div className="container">
          <div className="stories-container">
            <div className="stories-filters">
              <div className="stories-search-container">
                <div className="search-box">
                  <input
                    type="text"
                    placeholder={t("stories.search")}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="search-input"
                  />
                </div>
                {(searchQuery || selectedCategory) && (
                  <div className="search-results-info">
                    {t("stories.found", { count: postsData.total })}
                  </div>
                )}
              </div>

              {categories.length > 0 && (
                <div className="category-filters">
                  <button
                    className={`category-filter-btn ${selectedCategory === "" ? "active" : ""}`}
                    onClick={() => handleCategoryChange("")}
                  >
                    {t("stories.all")}
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category}
                      className={`category-filter-btn ${selectedCategory === category ? "active" : ""}`}
                      onClick={() => handleCategoryChange(category)}
                    >
                      {categoryName(t, category)}
                    </button>
                  ))}
                </div>
              )}

              {selectedCategory && categoryIntro(t, selectedCategory) && (
                <p className="category-intro">{categoryIntro(t, selectedCategory)}</p>
              )}
            </div>

            {filteredPosts.length === 0 ? (
              <div className="empty-state">
                <p>{searchQuery ? t("stories.noneSearch") : t("stories.none")}</p>
              </div>
            ) : (
              <>
                <div className="stories-list">
                  {filteredPosts.map((post) => (
                    <StoryCard key={post.id} post={post} />
                  ))}
                </div>

                {postsData.totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      {t("stories.previous")}
                    </button>
                    <span className="pagination-info">
                      {t("stories.page", { page: currentPage, total: postsData.totalPages })}
                    </span>
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === postsData.totalPages}
                    >
                      {t("stories.next")}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default StoriesList;
